import posthog from "posthog-js";
import { TRACKING_EVENTS } from "./posthog";
import { getPageViewEventId } from "./meta-capi";

// ============================================================================
// Configuration (depuis les env NEXT_PUBLIC_*, inlinées au build)
// ============================================================================

export const POSTHOG_KEY = process.env.NEXT_PUBLIC_POSTHOG_KEY ?? "";
export const POSTHOG_HOST = process.env.NEXT_PUBLIC_POSTHOG_HOST ?? "";

export const META_PIXEL_ID = process.env.NEXT_PUBLIC_META_PIXEL_ID ?? "";

// Google Ads : ID de compte "AW-XXXXXXXXX" + labels de conversion "AW-XXX/abc123"
export const GOOGLE_ADS_ID = process.env.NEXT_PUBLIC_GOOGLE_ADS_ID ?? "";
const GADS_LABEL_LEAD = process.env.NEXT_PUBLIC_GOOGLE_ADS_LABEL_LEAD ?? "";
const GADS_LABEL_APPLICATION =
  process.env.NEXT_PUBLIC_GOOGLE_ADS_LABEL_APPLICATION ?? "";

// ============================================================================
// Initialisation des pixels
// ============================================================================

// ============================================================================
// Étanchéité Meta / Google Ads sur les profils NON qualifiés
//   Règle produit : un lead en recherche d'emploi ou étudiant reste le
//   bienvenu à la masterclass — on lui donne le lien — mais on n'envoie AUCUNE
//   information le concernant aux régies. Deux raisons : on ne veut pas que
//   l'algorithme optimise vers ce profil, et on ne veut pas l'inclure dans les
//   audiences de retargeting.
//   Deux verrous complémentaires :
//     1. les pages de sortie « hors cible » n'initialisent pas le Pixel du tout
//        (donc pas même de PageView) ;
//     2. tout événement portant `qualified: false` ne sort jamais vers Meta ni
//        vers Google Ads — il ne part que dans PostHog, où on a besoin de le
//        voir pour mesurer le taux de disqualification.
// ============================================================================

/** Routes de sortie « hors cible » : aucun appel aux régies depuis ces pages. */
const PIXEL_MUTED_PATHS = ["/nok", "/mc/hc"];

const isPixelMutedPath = () =>
  typeof window !== "undefined" &&
  PIXEL_MUTED_PATHS.some((p) => window.location.pathname.startsWith(p));

export const initMetaPixel = () => {
  if (!META_PIXEL_ID || typeof window === "undefined") return;
  if (isPixelMutedPath()) return;
  if ((window as any).fbq) return;

  // @ts-ignore
  !function(f,b,e,v,n,t,s)
  // @ts-ignore
  {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
  // @ts-ignore
  n.callMethod.apply(n,arguments):n.queue.push(arguments)};
  // @ts-ignore
  if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
  // @ts-ignore
  n.queue=[];t=b.createElement(e);t.async=!0;
  // @ts-ignore
  t.src=v;s=b.getElementsByTagName(e)[0];
  // @ts-ignore
  s.parentNode.insertBefore(t,s)}(window, document,'script',
  'https://connect.facebook.net/en_US/fbevents.js');

  // @ts-ignore
  window.fbq("init", META_PIXEL_ID);
  // PageView avec event_id partagé → dédupliqué avec l'event serveur (CAPI).
  // @ts-ignore
  window.fbq("track", "PageView", {}, { eventID: getPageViewEventId() });
};

export const initGoogleAds = () => {
  if (!GOOGLE_ADS_ID || typeof window === "undefined") return;
  if (isPixelMutedPath()) return;
  if ((window as any).__gadsLoaded) return;
  (window as any).__gadsLoaded = true;

  const s = document.createElement("script");
  s.async = true;
  s.src = `https://www.googletagmanager.com/gtag/js?id=${GOOGLE_ADS_ID}`;
  document.head.appendChild(s);

  (window as any).dataLayer = (window as any).dataLayer || [];
  // gtag DOIT pousser les `arguments` bruts dans dataLayer
  function gtag(...args: any[]) {
    (window as any).dataLayer.push(args);
  }
  (window as any).gtag = gtag;
  gtag("js", new Date());
  gtag("config", GOOGLE_ADS_ID);
};

export const initPixels = () => {
  initMetaPixel();
  initGoogleAds();
};

// ============================================================================
// Mapping événement -> conversions Meta / Google Ads
//   PostHog reçoit TOUS les événements. Meta & Google Ads ne reçoivent que les
//   conversions configurées ci-dessous.
// ============================================================================

type ConversionConfig = {
  metaEvent?: string; // Standard event Meta (Lead, SubmitApplication…)
  gadsLabel?: string; // Conversion label Google Ads (AW-XXX/abc)
};

const CONVERSION_MAP: Record<string, ConversionConfig> = {
  [TRACKING_EVENTS.LEAD_QUALIFIED]: {
    metaEvent: "Lead",
    gadsLabel: GADS_LABEL_LEAD,
  },
  [TRACKING_EVENTS.APPLICATION_SUBMITTED]: {
    metaEvent: "SubmitApplication",
    gadsLabel: GADS_LABEL_APPLICATION,
  },
};

// ============================================================================
// API publique
// ============================================================================

/** Enregistre des super-properties PostHog (jointes à TOUS les events, persistées). */
export const registerSuperProps = (props: Record<string, any>) => {
  if (posthog && props && Object.keys(props).length) posthog.register(props);
};

/** Identifie l'utilisateur (PostHog) — à appeler dès qu'on a l'email. */
export const identifyLead = (
  email: string,
  properties?: Record<string, any>
) => {
  if (posthog) posthog.identify(email, properties);
};

/** Envoie un événement vers PostHog + (si conversion) Meta Pixel + Google Ads. */
export const trackEvent = (
  eventName: string,
  properties?: Record<string, any>
) => {
  // 1) PostHog — tous les événements
  if (posthog) posthog.capture(eventName, properties);

  if (typeof window === "undefined") return;

  // Verrou « hors cible » : jamais d'info sur un profil non qualifié aux régies.
  if (properties?.qualified === false || isPixelMutedPath()) return;

  const conversion = CONVERSION_MAP[eventName];
  if (!conversion) return;

  // 2) Meta Pixel
  if (conversion.metaEvent && (window as any).fbq) {
    (window as any).fbq("track", conversion.metaEvent, properties);
  }

  // 3) Google Ads
  if (conversion.gadsLabel && (window as any).gtag) {
    (window as any).gtag("event", "conversion", {
      send_to: conversion.gadsLabel,
      ...properties,
    });
  }
};
